import { AppButton, AppInput } from 'components/form';
import { AppCard, AppConfirmDialog, AppSpacer, AppUseIcon, AppUserTypeChip } from 'components/ui';
import { EmailShouldBeValid, ShouldNotBeEmpty } from 'utilities/Validations';
import { SingleUserType, UserTypes } from 'types/GlobalTypes';
import { useEffect, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import { OrganizationInvite } from 'types/models/OrganizationInvite';
import { SpriteStateIconsEnum } from 'types/Enums';
import { UserType } from 'types/ProfileTypes';
import defaultProfilePhotoUrl from 'assets/img/profile-pic-default.png';
import { formatDate } from 'utilities/DateUtils';
import { getColor } from 'utilities/SystemUtils';
import { t } from 'utilities/TranslationUtils';
import { useStores } from 'index';

enum DialogMode {
  InvitingNewUser = 'InvitingNewUser',
  EditingPendingInvite = 'EditingPendingInvite',
  EditingExistingUser = 'EditingExistingUser',
}

const UserDialog = (props: {
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: any, onSuccessFn: (e) => void, onErrorFn: (e) => void) => void;
  isDialogOpened: boolean;
  selectedUser: (SingleUserType & OrganizationInvite) | null;
  onRemoveUser: () => void;
}) => {
  const { usersStore, inviteStore, teamPortalApiAxiosInstance } = useStores();

  const [userEmail, setUserEmail] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [type, setUserType] = useState<UserType>();
  const [isDeleteWarningDisplayed, setIsDeleteWarningDisplayed] = useState(false);

  const onCloseDialogHandler = () => {
    if (props.isLoading) return;
    props.onClose();
    setTimeout(() => {
      setUserEmail('');
      setUserFirstName('');
      setUserLastName('');
      setUserType(undefined);
    }, 300);
  };

  useEffect(() => {
    if (props.selectedUser?.['inviteId']) {
      setUserEmail(props.selectedUser?.email || '');
      setUserFirstName(props.selectedUser?.firstName || '');
      setUserLastName(props.selectedUser?.lastName || '');
    }

    if (props.selectedUser?.id) {
      setUserEmail(props.selectedUser?.email);
      setUserFirstName(props.selectedUser?.firstName);
      setUserLastName(props.selectedUser?.lastName);
    } 

    if (props.selectedUser?.id && props.selectedUser?.type) {
      setUserType(props.selectedUser.type?.toLowerCase() !== UserType.Player ? UserType[props.selectedUser.type] : undefined);
    }
  }, [props.selectedUser]);

  // ======================================================================================================

  const searchForUser = (email: string, dataArray) => {
    return dataArray.find(
      (u: SingleUserType) =>
        email?.toLocaleUpperCase() === (u.email || '')?.toLocaleUpperCase() && (u.type || '')?.toLowerCase() !== UserType.Other
    );
  };

  const doesUserEmailExist = (email: string): boolean => {
    return searchForUser(email, usersStore.users);
  };
  const doesInvitationAlreadySent = (email: string): boolean => {
    return searchForUser(email, inviteStore.invites);
  };

  const isUserActive = () => props.selectedUser && props.selectedUser?.status === 'active';
  const isUserPending = () => props.selectedUser && props.selectedUser?.status?.toLowerCase().includes('pending') || false;
  // if user without status means that he should be invited first to be a user
  const isUserWithoutStatus = () => props.selectedUser && !props.selectedUser?.status;

  const getDialogMode = (): DialogMode => {
    if (!props.selectedUser) return DialogMode.InvitingNewUser;
    if (isUserWithoutStatus()) return DialogMode.InvitingNewUser; // User exists but needs invitation
    if (isUserPending()) return DialogMode.EditingPendingInvite;
    return DialogMode.EditingExistingUser;
  };

  const shouldShowNameFields = () => {
    const mode = getDialogMode();
    return mode === DialogMode.InvitingNewUser;
  };

  const shouldShowDeleteButton = () => {
    const mode = getDialogMode();
    return mode === DialogMode.EditingPendingInvite || mode === DialogMode.EditingExistingUser;
  };

  const shouldDisableFields = () => {
    const mode = getDialogMode();
    return props.isLoading || mode === DialogMode.EditingPendingInvite;
  };

  const shouldShowDuplicateEmailValidation = () => {
    const mode = getDialogMode();
    return mode === DialogMode.InvitingNewUser;
  };

  const onSubmitHandler = () => {
    if (props.isLoading) return;
    if (userEmail) {
      props.onSubmit(
        {
          ...props.selectedUser,
          email: userEmail,
          firstName: userFirstName,
          lastName: userLastName,
          type: 'FB.Coach',
        },
        onSuccessHandler,
        (e) => {}
      );
    }
  };

  const onSuccessHandler = (e) => {
    setUserEmail('');
    setUserFirstName('');
    setUserLastName('');
    setUserType(undefined);
    onCloseDialogHandler();
  };

  const onConfirmDeletingUserIfExists = async (selectedUser) => {
    if (props.isLoading || !selectedUser?.id) return;

    if (selectedUser.status === 'active') {
      handleDeletingPlayer(selectedUser.id);
      return;
    }

    handleCancellingInvitation(selectedUser.id);
  };

  const handleDeletingPlayer = async (invitationId) => {
    if (!invitationId) return;

    await usersStore.RemoveUser(teamPortalApiAxiosInstance, invitationId, handleRemovingUserSuccess, (err) => console.log({ err }));
  };

  const handleRemovingUserSuccess = () => {
    setIsDeleteWarningDisplayed(false);
    props.onRemoveUser();
  };

  const handleCancellingInvitation = async (invitationId) => {
    if (!invitationId) return;

    await inviteStore.CancelInvite(
      teamPortalApiAxiosInstance,
      invitationId,
      handleCancelingUserInviteSuccess,
      handleCancelingUserInviteError,
      handleCancelingUserInviteNotFound
    );
  };

  const handleCancelingUserInviteSuccess = (response) => {
    setIsDeleteWarningDisplayed(false);
    onCloseDialogHandler();
    props.onRemoveUser();
  };

  const handleCancelingUserInviteError = (response) => {
    console.log({ response });
  };

  const handleCancelingUserInviteNotFound = (response) => {
    console.log({ response });
  };

  const isSubmitDisabled = () => {
    // Common validations for all cases
    if (props.isLoading) return true;
    if (!userEmail) return true;
    if (EmailShouldBeValid(userEmail) === 'EMAIL_NOT_VALID') return true;

    const mode = getDialogMode();

    switch (mode) {
      case DialogMode.InvitingNewUser:
        if (!userFirstName || !userLastName) return true;
        if (doesUserEmailExist(userEmail) || doesInvitationAlreadySent(userEmail)) return true;
        return false;

      case DialogMode.EditingPendingInvite:
        return false; // Always allow resending

      case DialogMode.EditingExistingUser:
        if (!userFirstName || !userLastName) return true;
        if (!isUserEdited()) return true; // No changes made
        return false;
    }
  };

  const UserEmailShouldNotExist = (email) => !!doesUserEmailExist(email) && 'ErrorEmailExists';
  const UserEmailShouldNotAlreadyInvited = (email) => !!doesInvitationAlreadySent(email) && 'ErrorEmailInvited';

  const allowSendingInvitation = () => {
    const mode = getDialogMode();
    return mode === DialogMode.InvitingNewUser || isUserWithoutStatus();
  };

  // ======================================================================================================

  const isUserEdited = () => {
    if (!props.selectedUser) return false;

    if (props.selectedUser?.['inviteId'] && props.selectedUser?.email !== userEmail) return true;
    if (props.selectedUser?.id && props.selectedUser?.email !== userEmail) return true;

    if (props.selectedUser?.['inviteId'] && props.selectedUser?.firstName !== userFirstName) return true;
    if (props.selectedUser?.id && props.selectedUser?.firstName !== userFirstName) return true;

    if (props.selectedUser?.['inviteId'] && props.selectedUser?.lastName !== userLastName) return true;
    if (props.selectedUser?.id && props.selectedUser?.lastName !== userLastName) return true;

    if (props.selectedUser?.id && props.selectedUser?.type?.toLowerCase() === 'player' && !type) return false;
    if (props.selectedUser?.id && props.selectedUser?.type?.toLowerCase() !== type?.toLowerCase()) return true;

    return false;
  };

  const getSaveButtonLabel = () => {
    const mode = getDialogMode();

    switch (mode) {
      case DialogMode.InvitingNewUser:
        return t('common.sendInvitation');
      case DialogMode.EditingPendingInvite:
        return t('common.resendInvitation');
      case DialogMode.EditingExistingUser:
        return t('common.save');
    }
  };

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth
      className={`user-dialog ${props.isDialogOpened ? 'open' : ''}`}
      open={props.isDialogOpened}
      onClose={() => onCloseDialogHandler()}
      data-test='user-dialog'
    >
      <AppCard>
        <header className=' border-bottom d-flex justify-content-between'>
          <h3 className='font-18 font-500 text-uppercase  mb-0 text-nowrap d-flex align-items-center'>
            {props.selectedUser?.id ? t('inviteForm.editUser') : t('inviteForm.CreateNewUser')}
            {isUserEdited() && <span className='edited_indication ml-2'>{t('edited')}</span>}
          </h3>
        </header>
        {props.selectedUser && (isUserActive() || isUserPending() || isUserWithoutStatus()) ? (
          <>
            <article className='d-flex'>
              <img src={defaultProfilePhotoUrl} width='80px' height='80px' className='mr-2' alt='' />
              <section className='d-flex flex-column justify-content-center'>
                <p className='text-capitalize mb-1'>
                  {props.selectedUser.firstName?.trim()
                    ? `${props.selectedUser.firstName?.trim()} ${props.selectedUser.lastName?.trim()}`
                    : `[${props.selectedUser.email}]`}
                </p>

                <p className='text-capitalize mb-0 font-14 color-secondaryGrey d-flex align-items-center'>
                  <AppUserTypeChip userType={props.selectedUser.type as unknown as UserTypes} />
                  {props.selectedUser.organizationName && <span>, {props.selectedUser.organizationName}</span>}
                </p>
                {props.selectedUser?.status === 'pending' && (
                  <p className='mb-0'>
                    {t('common.invitationCreatedDate')} {formatDate(props.selectedUser.creationDate)}
                  </p>
                )}

                {props.selectedUser?.status && (
                  <div className='font-14 color-secondaryGrey d-flex align-items-center'>
                    <AppUseIcon name={`${SpriteStateIconsEnum[props.selectedUser.status?.toLowerCase()]}`} height='14' width='14' />
                    <span className={`ml-1 text-capitalize text-${getColor(props.selectedUser.status as any)}`}>
                      {t(`common.${props.selectedUser.status}`)}{' '}
                    </span>
                  </div>
                )}
              </section>
            </article>
            <AppSpacer space='30px' />
          </>
        ) : null}
        {/* Show name fields when inviting new user or editing user without status */}
        {shouldShowNameFields() ? (
          <>
          <div className="d-flex">
              <AppInput
                label={t('common.firstName')}
                name='userFirstName'
                value={userFirstName}
                type='text'
                validationRules={[
                  ShouldNotBeEmpty
                ]}
                {...(props.selectedUser?.status === 'pending' ? { hint: t('editInviteModal.ResendText') } : {})}
                onChange={(e) => setUserFirstName(e.target.value)}
                onKeyDown={(e) => {}}
                disabled={shouldDisableFields()}
                data-test='user-dialog-user-first-name'
              />
              <AppSpacer isHorizontal space='30px' />
              <AppInput
                label={t('common.lastName')}
                name='userLastName'
                value={userLastName}
                type='text'
                validationRules={[
                  ShouldNotBeEmpty
                ]}
                {...(props.selectedUser?.status === 'pending' ? { hint: t('editInviteModal.ResendText') } : {})}
                onChange={(e) => setUserLastName(e.target.value)}
                onKeyDown={(e) => {}}
                disabled={shouldDisableFields()}
                data-test='user-dialog-user-last-name'
              />
            </div>
            <AppSpacer space='30px' />
            <AppInput
              label={t('common.email')}
              name='userEmail'
              value={userEmail}
              type='email'
              validationRules={[
                ShouldNotBeEmpty,
                EmailShouldBeValid,
                ...(shouldShowDuplicateEmailValidation() ? [UserEmailShouldNotExist, UserEmailShouldNotAlreadyInvited] : []),
              ]}
              {...(props.selectedUser?.status === 'pending' ? { hint: t('editInviteModal.ResendText') } : {})}
              onChange={(e) => setUserEmail(e.target.value)}
              onKeyDown={(e) => {}}
              disabled={props.isLoading || isUserPending()}
              data-test='user-dialog-user-email'
            />
          </>
        ) : null}

        <footer className='pt-3 mt-4 border-top d-flex justify-content-between'>
          {shouldShowDeleteButton() ? (
            <AppButton
              variant='danger'
              onClick={() => setIsDeleteWarningDisplayed(true)}
              disabled={props.isLoading}
              data-test='user-dialog__delete-button'
            >
              {t('common.delete')}
            </AppButton>
          ) : (
            <div></div>
          )}
          <div>
            <AppButton
              variant='secondary'
              onClick={() => onCloseDialogHandler()}
              disabled={props.isLoading}
              data-test='user-dialog__cancel-button'
            >
              {isUserActive() ? t('common.done') : t('common.cancel')}
            </AppButton>
            {!isUserActive() && (
              <AppButton
                disabled={isSubmitDisabled()}
                variant='primary'
                className=' ml-2'
                onClick={() => onSubmitHandler()}
                data-test='user-dialog__submit-button'
              >
                {props.isLoading ? `${t('common.loading')}...` : <>{getSaveButtonLabel()}</>}
              </AppButton>
            )}
          </div>
        </footer>
        <AppConfirmDialog
          title={t('common.deleteUser')}
          message={t('common.removeUserMessage')}
          isDialogOpen={isDeleteWarningDisplayed}
          onConfirm={() => onConfirmDeletingUserIfExists(props.selectedUser)}
          isLoading={props.isLoading}
          onCancel={() => setIsDeleteWarningDisplayed(false)}
        />
      </AppCard>
    </Dialog>
  );
};

export default UserDialog;
